import { html } from '../../html.js';
import type { VNode } from '../../html.js';
import type { Verdict } from '../api.js';

/**
 * The small pieces every screen repeats.
 *
 * Two of these carry a product rule rather than a style. `Verdict` renders
 * `unknown` as the words "never checked" instead of a neutral grey tick, because
 * a tenant nobody has probed and a tenant that passed its probe are not the same
 * fact and the Sites table is where that distinction is worth money. `Scroller`
 * exists because wide content is the only thing that has ever made this layout
 * overflow the document — a table, a run result, a code block — and each one is
 * therefore required to scroll inside its own box.
 */

export function Scroller(props: { children?: unknown; label?: string }): VNode {
  // tabindex makes a scroll container reachable by keyboard; without it a
  // keyboard user cannot pan a table that is wider than the viewport.
  return html`<div
    class="scroller"
    tabindex="0"
    role="group"
    aria-label=${props.label ?? 'Scrollable content'}
  >
    ${props.children}
  </div>`;
}

export function Verdict(props: { verdict: Verdict; checkedAt?: string | null }): VNode {
  // The dot carries the colour; the word carries the meaning for anyone the
  // colour does not reach. `checkedAt` rides on the title because the Sites
  // table no longer spends a column on it, and "healthy" without a date is a
  // claim rather than an observation.
  if (props.verdict === 'unknown') {
    return html`<span class="verdict verdict--unknown" title="No probe has ever run against this site."
      ><i class="verdict__dot" aria-hidden="true"></i>Never checked</span
    >`;
  }
  const label =
    props.verdict === 'healthy' ? 'Healthy' : props.verdict === 'degraded' ? 'Degraded' : 'Broken';
  return html`<span
    class=${`verdict verdict--${props.verdict}`}
    title=${props.checkedAt ? `Last checked ${props.checkedAt}` : undefined}
    ><i class="verdict__dot" aria-hidden="true"></i>${label}</span
  >`;
}

export function Stat(props: { label: string; value: string | number; note?: string }): VNode {
  return html`
    <div class="stat">
      <p class="stat__label">${props.label}</p>
      <p class="stat__value">${props.value}</p>
      ${props.note ? html`<p class="stat__note">${props.note}</p>` : null}
    </div>
  `;
}

/** A failure the operator caused or can act on, announced the moment it appears. */
export function ErrorNote(props: { message: string | null; label?: string }): VNode | null {
  if (!props.message) return null;
  return html`<p class="errnote" role="alert">
    ${props.label ? html`<b>${props.label}</b> ` : null}${props.message}
  </p>`;
}

/**
 * An empty state with a way out of it.
 *
 * Every one of these used to be a single grey sentence, and several of them
 * described a next step without linking to it — "Publish one from the
 * onboarding console" on a screen with no link to the console. A person reading
 * an empty state is reading it because they want the thing that is missing, so
 * the action is part of the component and a caller that genuinely has nothing
 * to offer has to leave it out on purpose rather than by omission.
 */
export function Empty(props: {
  title: string;
  body?: string;
  action?: { label: string; href: string };
}): VNode {
  return html`
    <div class="empty">
      <p class="empty__title">${props.title}</p>
      ${props.body ? html`<p class="empty__body">${props.body}</p>` : null}
      ${props.action
        ? html`<a class="btn btn--sm" href=${props.action.href}>${props.action.label}</a>`
        : null}
    </div>
  `;
}

/**
 * The shape of the content that is coming, while it is coming.
 *
 * A "Loading…" sentence and then a table is two different layouts one after the
 * other, and the jump is what makes a fast load feel slow. The bars are the
 * rows that are about to arrive. It is `aria-hidden` with a live region beside
 * it because a screen reader needs the word, not the bars.
 */
export function Skeleton(props: { rows?: number; label?: string }): VNode {
  const rows = props.rows ?? 4;
  return html`
    <div class="skels">
      <p class="sr" role="status">${props.label ?? 'Loading…'}</p>
      <div class="skels__bars" aria-hidden="true">
        ${Array.from({ length: rows }, (_, i) => html`<div class="skel" key=${i}></div>`)}
      </div>
    </div>
  `;
}

/**
 * A read that failed, with the retry next to it.
 *
 * Distinct from `ErrorNote`, which announces something the operator just did
 * and can see the consequence of. This one replaces content that is not there,
 * so it carries the button that would put it there — an error message with no
 * retry is a dead end that costs a page reload to leave.
 */
export function ErrorState(props: {
  message: string;
  label?: string;
  body?: string;
  onRetry?: () => void;
}): VNode {
  return html`
    <div class="errstate" role="alert">
      <p class="errstate__h">${props.label ?? 'That did not load.'}</p>
      <p class="errstate__msg">${props.message}</p>
      ${props.body ? html`<p class="errstate__body">${props.body}</p>` : null}
      ${props.onRetry
        ? html`<button type="button" class="btn btn--sm" onClick=${() => props.onRetry?.()}>
            Try again
          </button>`
        : null}
    </div>
  `;
}

export function Pill(props: { tone?: 'plain' | 'warn' | 'good'; children?: unknown }): VNode {
  return html`<span class=${`pill pill--${props.tone ?? 'plain'}`}>${props.children}</span>`;
}

export function fmtInt(n: number): string {
  return n.toLocaleString('en-GB');
}

export function fmtMs(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(ms < 10_000 ? 2 : 1)} s`;
}

export function fmtTtl(sec: number): string {
  if (sec % 3600 === 0 && sec >= 3600) return `${sec / 3600} h`;
  if (sec % 60 === 0 && sec >= 60) return `${sec / 60} min`;
  return `${sec} s`;
}

/**
 * A timestamp as "3 h ago", with the exact value on the title attribute.
 *
 * Absent is rendered by the CALLER, not here: a function that turns null into
 * "never" would let a caller print "never" for a field it simply forgot to read.
 */
export function ago(iso: string, now: number): string {
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return iso;
  const sec = Math.max(0, Math.round((now - then) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86_400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86_400)}d ago`;
}

export function When(props: { iso: string | null; now: number; absent: string }): VNode {
  if (!props.iso) return html`<span class="when when--never">${props.absent}</span>`;
  return html`<span class="when" title=${props.iso}>${ago(props.iso, props.now)}</span>`;
}
